import { createSelector } from 'reselect';
import { fromJS, List, Map } from 'immutable';
import toDate from 'date-fns/toDate';

import { getActiveBookId, getActiveSpreadId } from 'redux/selectors/app';
import { getChildBirthday, getExtraSpreadIds } from 'redux/selectors/orders';

export const getAllBooks = state => state.get('books', List());
const getSpreadId = (state, spreadId) => spreadId || getActiveSpreadId(state);
const getWithExtras = (state, withExtras) => withExtras || false;

export const getCurrentBook = createSelector([getAllBooks, getActiveBookId], (books, bookId) => {
  return (books && books.find && books.find(book => book.get('id') === bookId)) || Map();
});

export const getYearOffset = createSelector([getCurrentBook], book => {
  return book.get('calendar_year_offset', 0);
});

export const getPrintType = state => {
  return getCurrentBook(state).get('print_type') || 'book';
};

export const isCalendar = book => {
  return book.get('book_type') === 'calendar';
};

export const getCurrentBookSpreads = createSelector(
  [getCurrentBook, getExtraSpreadIds, getChildBirthday, getWithExtras, getYearOffset],
  (book, extraSpreadIds, childBirthday, withExtras, yearOffset) => {
    let spreads = book.get('spreads', List());

    if (isCalendar(book)) {
      const groupedSpreads = book
        .get('spreads')
        .toJS()
        .reduce(
          (carry, spread) => {
            if (spread.month > -1) {
              if (spread.month === 0) {
                carry.calendarStartIndex = carry.normal.length - 1;
              }

              if (!(spread.month in carry.months)) {
                carry.months[spread.month] = [];
              }

              carry.months[spread.month].push(spread);
            } else {
              carry.normal.push(spread);
            }

            return carry;
          },
          {
            calendarStartIndex: 0,
            normal: [],
            months: [],
          }
        );

      const birthday = childBirthday ? toDate(childBirthday) : new Date();

      const birthdayMonth = new Date(birthday.getFullYear(), birthday.getMonth());

      const end = groupedSpreads.months.slice(0, birthday.getMonth());
      const beginning = groupedSpreads.months.slice(birthday.getMonth());

      const monthSpreads = beginning.concat(end, [beginning[0]]).reduce((carry, monthSpreads, monthIndex) => {
        const monthDate = new Date(birthdayMonth);
        monthDate.setFullYear(birthdayMonth.getFullYear() + yearOffset);
        monthDate.setMonth(birthdayMonth.getMonth() + monthIndex);

        const year = monthDate.getFullYear();

        carry.push(
          ...monthSpreads.map((monthSpread, monthSpreadIndex) => ({
            ...monthSpread,
            monthIndex,
            monthSpreadIndex,
            actualId: monthSpread.id,
            id: `${monthSpread.id}.${year}`,
            label: `${monthSpread.label} (${year})`,
            isMonthSpread: true,
            spreadDate: monthDate,
            moments: monthSpread.moments.map(moment => ({
              ...moment,
              id: `${moment.id}.${year}`,
            })),
          }))
        );

        return carry;
      }, []);

      const spreadsStart = groupedSpreads.normal.slice(0, groupedSpreads.calendarStartIndex + 1);
      const spreadsEnd = groupedSpreads.normal.slice(groupedSpreads.calendarStartIndex + 1);

      spreads = fromJS(spreadsStart.concat(monthSpreads, spreadsEnd));
    }

    return withExtras
      ? spreads
      : spreads.filter(spread => spread.get('type') === 'default' || extraSpreadIds.indexOf(spread.get('id')) > -1);
  }
);

const getCurrentBookSpreadsWithExtras = state => getCurrentBookSpreads(state, true);

export const getCurrentBookDesigns = createSelector([getCurrentBook], book => {
  return book.get('designs', List());
});

export const getSpreadInfo = createSelector(
  [getCurrentBookSpreads, getSpreadId],
  (spreads, spreadId) => spreads.find(spread => spread.get('id') === spreadId) || Map()
);

export const getAvailableMoments = createSelector([getCurrentBookSpreads, getSpreadId], (spreads, spreadId) => {
  const spread = spreads.find(spread => spread.get('id') === spreadId) || Map();
  const moments = spread.get('moments', List()); //.filter(moment => moment.get('type') === 'photo');

  return moments;
});

export const getExtraSpreadIdAfter = createSelector(
  [getCurrentBookSpreadsWithExtras, getSpreadId, getExtraSpreadIds],
  (spreads, spreadId, extraSpreadIds) => {
    const currentSpreadIndex = spreads.findIndex(spread => spread.get('id') === spreadId);
    const nextSpread = spreads.get(currentSpreadIndex + 1);
    const alreadyUsed = nextSpread ? extraSpreadIds.indexOf(nextSpread.get('id')) > -1 : false;

    return !alreadyUsed && nextSpread && nextSpread.get('type') === 'extra' ? nextSpread.get('id') : null;
  }
);

export const isExtraSpread = createSelector([getCurrentBookSpreads, getSpreadId], (spreads, spreadId) => {
  const currentSpread = spreads.find(spread => spread.get('id') === spreadId);
  return currentSpread && currentSpread.get('type') === 'extra';
});



// WEBPACK FOOTER //
// ./app/redux/selectors/books.js